<template>
  <div id="home">
    <FullscreenLoader v-if="!$store.state.route.from.name " :show="loading" />
    <div v-if="!loading">
      <div v-for="(component, index) in componentList" :key="index">
        <!-- Search Bar Section -->
        <lazy-hydrate v-if="isSearchBar(component)">
          <section
            class="container pb30"
          >
            <div class="col-md-12 start-xs">
              <search-bar
                :category-list="component.categoryList"
                :subfilters="false"
                :current-category="null"
              />
            </div>
          </section>
        </lazy-hydrate>
        <!-- Tile Rows Section -->
        <lazy-hydrate v-if="isTileRow(component)" when-visible>
          <div
            class="container"
          >
            <advertising-tiles :partner-tiles="component.tileList" />
          </div>
        </lazy-hydrate>
        <!-- Banner Row Section -->
        <lazy-hydrate v-if="isBannerRow(component)" when-visible>
          <section
            class="pt10 pb25"
          >
            <div class="container hidden-xs">
              <static-page-tiles :partner-tiles="component.bannerList" />
            </div>
          </section>
        </lazy-hydrate>
        <!-- Slider Section -->
        <lazy-hydrate v-if="isSlider(component)" when-visible>
          <main-slider
            :partner-slides="component.slideList"
          >
            <slot name="" />
          </main-slider>
        </lazy-hydrate>
      </div>
    </div>
  </div>
</template>
<script>
// query constructor
import { isServer, onlineHelper } from '@vue-storefront/core/helpers'
import LazyHydrate from 'vue-lazy-hydration'

// Core pages
import Home from '@vue-storefront/core/pages/Home'
// Theme core components
import MainSlider from 'theme/components/core/blocks/MainSlider/MainSlider'
import SearchBar from 'theme/components/theme/anyrent/SearchBar/SearchBar'
// Theme local components
import {Logger} from '@vue-storefront/core/lib/logger'
import {mapGetters, mapState} from 'vuex'
import config from 'config'
import {registerModule} from '@vue-storefront/core/lib/modules'
import {RecentlyViewedModule} from '@vue-storefront/core/modules/recently-viewed'
import AdvertisingTiles from '../components/theme/blocks/Home/AdvertisingTiles'
import StaticPageTiles from '../components/theme/blocks/Home/StaticPageTiles'
import NoSSR from 'vue-no-ssr'
import { selfCanonical } from '../helpers/meta/selfCanonical';
import HOMEPAGE_QUERY from '../../../../src/themes/default/graphql/strapi/queries/homepage.gql'
import i18n from '@vue-storefront/i18n'
import FullscreenLoader from 'theme/components/core/FullscreenLoader.vue'
import { getApolloProvider } from '@vue-storefront/core/scripts/resolvers/resolveGraphQL'
import { getCmsPartnerId } from '../helpers';

const homepageComponentTypeMap = {
  ComponentHomepageSearchBar: 'ComponentHomepageSearchBar',
  ComponentHomepageTileRow: 'ComponentHomepageTileRow',
  ComponentHomepageBannerRow: 'ComponentHomepageBannerRow',
  ComponentHomepageSlider: 'ComponentHomepageSlider'
}

const transformHomepageDataQueryResponse = (homepageQueryResponse) => {
  const homepageComponents = homepageQueryResponse?.data?.homepages?.data?.[0]?.attributes?.Homepage || [];
  const componentList = homepageComponents
    .map((component) => {
      switch (component.__typename) {
        case homepageComponentTypeMap.ComponentHomepageSearchBar:
          return {
            type: component.__typename,
            categoryList: (component.Category || []).map(({ id, Link, Name }) => ({
              id,
              link: Link,
              name: Name
            }))
          };

        case homepageComponentTypeMap.ComponentHomepageTileRow:
          if (!component.Tile?.length) {
            return null;
          }

          return {
            type: component.__typename,
            tileList: component.Tile.map(({ id, Text, Link, ButtonText, BackgroundImage, ImageAltText, BackgroundColor }) => ({
              id,
              text: Text,
              link: Link,
              buttonText: ButtonText,
              backgroundImageUrl: BackgroundImage?.data?.attributes.url,
              imageAltText: ImageAltText,
              backgroundColor: BackgroundColor
            }))
          }

        case homepageComponentTypeMap.ComponentHomepageBannerRow:
          if (!component.Banner?.length) {
            return null
          }

          return {
            type: component.__typename,
            bannerList: component.Banner.map(({ id, Text, Link, Icon, IconAltText, BackgroundColor }) => ({
              id,
              text: Text,
              link: Link,
              iconUrl: Icon?.data?.attributes.url,
              iconAltText: IconAltText,
              backgroundColor: BackgroundColor
            }))
          }

        case homepageComponentTypeMap.ComponentHomepageSlider:
          if (!component.Slide?.length) {
            return null;
          }

          return {
            type: component.__typename,
            slideList: component.Slide.map(({ id, Title, Link, Subtitle, ImageAltText, BackgroundImage }) => ({
              id,
              title: Title,
              link: Link,
              subtitle: Subtitle,
              imageAltText: ImageAltText,
              backgroundImageUrl: BackgroundImage?.data?.attributes.url
            }))
          }

        default:
          return null;
      }
    })
    .filter((component) => !!component);

  return componentList;
}

export default {
  data () {
    return {
      loading: true,
      componentList: []
    }
  },
  mixins: [Home],
  components: {
    SearchBar,
    AdvertisingTiles,
    StaticPageTiles,
    MainSlider,
    LazyHydrate,
    FullscreenLoader
  },
  methods: {
    async fetchHomepageData (partnerId) {
      try {
        const homepageQueryResponse = await this.$apollo.query({
          query: HOMEPAGE_QUERY,
          variables: {
            'filters': {
              'Partner': {
                'Name': {
                  'eq': partnerId
                }
              }
            }
          }
        });

        this.componentList = transformHomepageDataQueryResponse(homepageQueryResponse);
      } catch (error) {
        Logger.error('Cannot load data on homepage: ' + error)();
        throw error;
      }
    },
    isSearchBar (component) {
      return component.type === homepageComponentTypeMap.ComponentHomepageSearchBar;
    },
    isTileRow (component) {
      return component.type === homepageComponentTypeMap.ComponentHomepageTileRow;
    },
    isBannerRow (component) {
      return component.type === homepageComponentTypeMap.ComponentHomepageBannerRow;
    },
    isSlider (component) {
      return component.type === homepageComponentTypeMap.ComponentHomepageSlider;
    }
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('homepage', ['getEverythingNewCollection']),
    ...mapState({
      storeView: state => state.storeView
    }),
    cmsPartnerId () {
      return getCmsPartnerId(this.storeView);
    },
    categories () {
      const cats = this.getCategories
      return cats
    },
    isOnline () {
      return onlineHelper.isOnline
    },
    isLazyHydrateEnabled () {
      return config.ssr.lazyHydrateFor.some(
        field => ['homepage', 'homepage.new_collection'].includes(field)
      )
    }
  },
  beforeCreate () {
    registerModule(RecentlyViewedModule)
  },
  async beforeMount () {
    if (this.$store.state.__DEMO_MODE__) {
      const onboardingClaim = await this.$store.dispatch('claims/check', { claimCode: 'onboardingAccepted' })
      if (!onboardingClaim) { // show onboarding info
        this.$bus.$emit('modal-toggle', 'modal-onboard')
        this.$store.dispatch('claims/set', { claimCode: 'onboardingAccepted', value: true })
      }
    }
  },
  mounted () {
    this.fetchHomepageData(this.cmsPartnerId)
      .catch(() => {})
      .then(() => {
        this.loading = false;
      });
    if (!this.isLoggedIn && localStorage.getItem('redirect')) this.$bus.$emit('modal-show', 'modal-signup')
  },
  serverPrefetch () {
    return this.fetchHomepageData(this.cmsPartnerId);
  },
  watch: {
    isLoggedIn () {
      const redirectObj = localStorage.getItem('redirect')
      if (redirectObj) this.$router.push(redirectObj)
      localStorage.removeItem('redirect')
    }
  },
  async asyncData ({ store, route, app }) { // this is for SSR purposes to prefetch data
    Logger.info('Calling asyncData in Home (theme)')()
    const apolloProvider = await getApolloProvider();
    const apolloClient = apolloProvider.defaultClient;
    try {
      const response = await apolloClient.query({
        query: HOMEPAGE_QUERY,
        variables: {
          filters: {
            Partner: {
              Name: {
                eq: getCmsPartnerId(store.state.storeView)
              }
            }
          }
        }
      });

      const componentList = transformHomepageDataQueryResponse(response)

      await Promise.all([
        store.dispatch('homepage/fetchNewCollection'),
        store.dispatch('promoted/updateHeadImage'),
        store.dispatch('promoted/updatePromotedOffers')
      ])

      return {
        componentList
      };
    } catch (error) {
      console.error('Error in asyncData for Home: ', error);
      return { componentList: [] };
    }
  },
  beforeRouteEnter (to, from, next) {
    if (!isServer && !from.name) { // Loading products to cache on SSR render
      next(vm =>
        vm.$store.dispatch('homepage/fetchNewCollection').catch(error => {
          Logger.error('Cannot load homepage new collection: ' + error)()
        }).then(() => {
          vm.loading = false
        })
      )
    } else {
      next()
    }
  },
  metaInfo () {
    return {
      title: this.$route.meta.title || i18n.t('Home Page'),
      meta: this.$route.meta.description ? [{ vmid: 'description', name: 'description', content: this.$route.meta.description }] : [],
      link: [
        selfCanonical(this.$route)
      ]
    }
  }
}
</script>
